"use client";

import { createContext, type ReactNode, useCallback, useContext, useRef } from "react";
import type { ChatTextReference, ExplainContextType } from "./ExplainProvider.types";

const ExplainContext = createContext<ExplainContextType | null>(null);

interface PendingExplainRequest {
  text: string;
  references: ChatTextReference[];
}

export const ExplainProvider = ({ children }: { children: ReactNode }) => {
  const handleExplainRequestRef = useRef<((text: string, references: ChatTextReference[]) => Promise<void>) | null>(
    null,
  );
  const pendingRequestRef = useRef<PendingExplainRequest | null>(null);

  const registerExplainHandler = useCallback(
    (handler: (text: string, references: ChatTextReference[]) => Promise<void>) => {
      handleExplainRequestRef.current = handler;

      // Execute any pending explain request after the current render cycle
      // Use the ref to get the latest handler in case it was updated
      if (pendingRequestRef.current) {
        const { text, references } = pendingRequestRef.current;
        pendingRequestRef.current = null;
        setTimeout(() => {
          if (handleExplainRequestRef.current) {
            handleExplainRequestRef.current(text, references);
          }
        }, 0);
      }
    },
    [],
  );

  const requestExplain = useCallback(async (text: string, references: ChatTextReference[]) => {
    if (handleExplainRequestRef.current) {
      await handleExplainRequestRef.current(text, references);
    } else {
      // Queue the request for when the handler is registered
      pendingRequestRef.current = { text, references };
    }
  }, []);

  return (
    <ExplainContext.Provider
      value={{
        registerExplainHandler,
        requestExplain,
      }}
    >
      {children}
    </ExplainContext.Provider>
  );
};

export const useExplain = () => {
  const context = useContext(ExplainContext);
  if (!context) {
    throw new Error("useExplain must be used within ExplainProvider");
  }
  return context;
};
